import React, { useState, useEffect } from 'react';
import { fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { apiService } from '../../services/api';
import './UserProfileForm.css';

interface UserProfile {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  title: string;
}

interface UserProfileFormProps {
  onCancel: () => void;
}

const countryCodes = [
  { code: '+1', country: 'United States/Canada' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+31', country: 'Netherlands' },
  { code: '+32', country: 'Belgium' },
  { code: '+41', country: 'Switzerland' },
  { code: '+43', country: 'Austria' },
  { code: '+45', country: 'Denmark' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+358', country: 'Finland' },
  { code: '+353', country: 'Ireland' },
  { code: '+351', country: 'Portugal' },
  { code: '+30', country: 'Greece' },
  { code: '+48', country: 'Poland' },
  { code: '+420', country: 'Czech Republic' },
  { code: '+36', country: 'Hungary' },
  { code: '+91', country: 'India' },
  { code: '+86', country: 'China' },
  { code: '+81', country: 'Japan' },
  { code: '+82', country: 'South Korea' },
  { code: '+61', country: 'Australia' },
  { code: '+64', country: 'New Zealand' },
  { code: '+27', country: 'South Africa' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+54', country: 'Argentina' },
];

const knownCountryCodes = [
  '+1', '+7', '+20', '+27', '+30', '+31', '+32', '+33', '+34', '+36',
  '+39', '+40', '+41', '+43', '+44', '+45', '+46', '+47', '+48', '+49',
  '+52', '+54', '+55', '+60', '+61', '+62', '+63', '+64', '+65', '+66',
  '+81', '+82', '+84', '+86', '+90', '+91', '+92', '+94', '+351', '+353',
  '+354', '+358', '+420', '+421', '+886', '+966', '+971', '+972',
];

const UserProfileForm: React.FC<UserProfileFormProps> = ({ onCancel }) => {
  const [step, setStep] = useState<'profile' | 'verify-phone'>('profile');
  const [formData, setFormData] = useState<UserProfile>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    title: '',
  });
  const [countryCode, setCountryCode] = useState('+44');
  const [originalPhone, setOriginalPhone] = useState('');
  const [region, setRegion] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof UserProfile, string>>>({});
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const titles = ['Mr', 'Mrs', 'Ms', 'Dr'];

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setFetchingProfile(true);
        const attributes = await fetchUserAttributes();

        const phoneNumber = attributes.phone_number || '';
        let phoneWithoutCode = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;
        let extractedCountryCode = '+44';

        if (phoneNumber.startsWith('+')) {
          const matched = knownCountryCodes
            .filter(c => phoneNumber.startsWith(c))
            .sort((a, b) => b.length - a.length)[0];
          if (matched) {
            extractedCountryCode = matched;
            phoneWithoutCode = phoneNumber.substring(matched.length);
          }
        }

        setCountryCode(extractedCountryCode);
        setOriginalPhone(phoneNumber);
        setRegion(attributes['custom:region'] || '');
        setFormData({
          firstName: attributes.given_name || attributes.name || '',
          lastName: attributes.family_name || '',
          phoneNumber: phoneWithoutCode,
          title: attributes['custom:title'] || '',
        });
      } catch (error) {
        console.error('Failed to fetch user attributes:', error);
        setErrorMessage('Failed to load profile information. Please try again.');
      } finally {
        setFetchingProfile(false);
      }
    };

    loadUserProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    let sanitizedValue = value;

    if (name === 'phoneNumber') {
      sanitizedValue = value.replace(/\D/g, '');
      if (sanitizedValue.length > 15) {
        sanitizedValue = sanitizedValue.slice(0, 15);
      }
    } else if (name === 'firstName' || name === 'lastName') {
      if (value.length > 50) {
        sanitizedValue = value.slice(0, 50);
      }
    }

    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    setErrors(prev => ({ ...prev, [name]: '' }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserProfile, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (formData.phoneNumber.length < 7 || formData.phoneNumber.length > 15) {
      newErrors.phoneNumber = 'Phone number must be between 7 and 15 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const newPhone = `${countryCode}${formData.phoneNumber}`;
      const phoneChanged = newPhone !== originalPhone;

      await updateUserAttributes({
        userAttributes: {
          'custom:title': formData.title,
          name: formData.firstName,
          family_name: formData.lastName,
          given_name: formData.firstName,
          phone_number: newPhone,
        },
      });

      if (phoneChanged) {
        await apiService.sendPhoneOtp(newPhone);
        setStep('verify-phone');
      } else {
        setSuccessMessage('Profile updated successfully!');
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      setErrorMessage(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');

    if (!otpCode || otpCode.length < 6) {
      setOtpError('Please enter the 6-digit code sent to your phone.');
      return;
    }

    setLoading(true);
    try {
      const newPhone = `${countryCode}${formData.phoneNumber}`;
      await apiService.verifyPhoneOtp(otpCode, newPhone);
      setOriginalPhone(newPhone);
      setOtpCode('');
      setStep('profile');
      setSuccessMessage('Profile updated and phone number verified!');
    } catch (error: any) {
      console.error('Phone verification error:', error);
      setOtpError(error.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setOtpError('');
    setResendLoading(true);
    try {
      await apiService.sendPhoneOtp(`${countryCode}${formData.phoneNumber}`);
    } catch (error: any) {
      setOtpError(error.message || 'Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (fetchingProfile) {
    return (
      <div className="user-profile-form-container">
        <div className="loading-state">
          <span className="loading-spinner"></span>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (step === 'verify-phone') {
    return (
      <div className="user-profile-form-container">
        <h2 className="form-title">Verify New Phone Number</h2>
        <p className="form-description">
          We sent a 6-digit code to {countryCode} {formData.phoneNumber}. Enter it below to confirm your new number.
        </p>

        <form onSubmit={handleVerifyPhone} className="user-profile-form">
          <div className="form-group">
            <label className="form-label" htmlFor="otpCode">Verification Code</label>
            <input
              type="text"
              id="otpCode"
              className={`form-input ${otpError ? 'error' : ''}`}
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setOtpError('');
              }}
              maxLength={6}
              placeholder="123456"
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
            {otpError && <span className="form-error">{otpError}</span>}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleResendCode}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending...' : 'Resend Code'}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <span className="loading-spinner"></span> : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="user-profile-form-container">
      <h2 className="form-title">My Profile</h2>
      <p className="form-description">
        Update your profile information below
      </p>

      <form onSubmit={handleSubmit} className="user-profile-form">
        <div className="profile-form-columns">
          <div className="profile-form-column">
            <h3 className="column-heading">Personal Details</h3>

            <div className="form-group">
              <label className="form-label" htmlFor="title">Title</label>
              <select
                id="title"
                name="title"
                className={`form-input ${errors.title ? 'error' : ''}`}
                value={formData.title}
                onChange={handleChange}
              >
                <option value="">Select title (optional)</option>
                {titles.map((title) => (
                  <option key={title} value={title}>
                    {title}
                  </option>
                ))}
              </select>
              {errors.title && <span className="form-error">{errors.title}</span>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className={`form-input ${errors.firstName ? 'error' : ''}`}
                value={formData.firstName}
                onChange={handleChange}
                maxLength={50}
                placeholder="John"
              />
              {errors.firstName && <span className="form-error">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="lastName">
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className={`form-input ${errors.lastName ? 'error' : ''}`}
                value={formData.lastName}
                onChange={handleChange}
                maxLength={50}
                placeholder="Doe"
              />
              {errors.lastName && <span className="form-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="profile-column-divider" />

          <div className="profile-form-column">
            <h3 className="column-heading">Contact & Storage</h3>

            {region && (
              <div className="form-group">
                <label className="form-label">Data Crate Region</label>
                <input
                  type="text"
                  className="form-input form-input-readonly"
                  value={region}
                  readOnly
                />
                <small className="form-hint">Cannot be changed after registration.</small>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="countryCode">Country Code</label>
              <select
                id="countryCode"
                className="form-input"
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setSuccessMessage('');
                  setErrorMessage('');
                }}
              >
                {countryCodes.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="phoneNumber">
                Phone Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                value={formData.phoneNumber}
                onChange={handleChange}
                maxLength={15}
                placeholder="7911123456"
              />
              {errors.phoneNumber && <span className="form-error">{errors.phoneNumber}</span>}
              <small className="form-hint">
                Without country code (7–15 digits). Changing this requires re-verification.
              </small>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="success-message" role="alert">
            ✓ {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="error-message" role="alert">
            ✗ {errorMessage}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? <span className="loading-spinner"></span> : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileForm;
